/**
 * OUTFLOW: Sales AR, sitting under the margin scheme.
 *
 * Winnie's v9 note, in red: "I want a sales AR in as a table in the side tool bar as well
 * under the margin scheme (GST Margin) — all this information should come for the deal
 * pipeline in the sold status."
 *
 * Three things follow from that sentence, and all three are the point of this file.
 *
 * 1. It reads the DEAL PIPELINE, not the invoice table. The existing Sales Invoice Report
 *    lists finalised invoices; there are two of those, neither against a sold vehicle, so
 *    that screen reads empty while 153 cars have in fact been sold. A register driven off
 *    the pipeline shows the sales that actually happened.
 *
 * 2. The GST is margin-scheme GST, and it is NOT recomputed here. assessCredit() in
 *    lib/gstTreatment is the one implementation of Division 66 — the tax-invoice case, the
 *    notional credit, and the cap — and this file asks it rather than dividing by eleven on
 *    its own. Net GST on a sale is the GST charged on that sale less the credit the sale
 *    releases: one eleventh of the margin where the margin is positive, and nil where the
 *    car sold at a loss, because the notional credit cannot exceed the GST on the sale.
 * *
 *    A WORD ON WHAT THESE FIGURES ARE. The GST Register releases a Division 66 credit
 *    only on a REALISED sale - one with an invoice behind it - because a recorded sale
 *    price with no invoice is an intention, and an intention must never move the number
 *    that goes on a BAS. This screen draws the same line: every row carries a basis, and
 *    the totals separate netGstRealised from netGstProjected. The realised figure equals
 *    what the register reports for the same period. The first version of this file did
 *    not make that distinction and presented the whole projected forecast as though it
 *    were payable - same shared rule, different definition of sold, which is exactly how
 *    two screens come to disagree.
 * 3. Received and outstanding are stated only where a sales invoice exists. A vehicle sold
 *    and settled months ago has no invoice record in this platform, and printing its full
 *    sale price as "outstanding" would invent a debt that nobody owes.
 */

import { supabase } from '../lib/supabase'
import { closeOutVerdict } from '../lib/closeOut'
import { dischargeOnFullyPaid } from './ppsrDischargeService'
import { loadPlatformDates } from './platformSettingsService'
import { DateRange, ALL_TIME, inRange, rangeLabel } from '../lib/dateRange'
import { assessCredit, gstComponent, GST_TREATMENT_LABEL } from '../lib/gstTreatment'
import { GstTreatment } from '../types'
import { SOLD_STATUSES } from '../lib/dealLifecycle'
import {
  amountOutstanding,
  onChaseList,
  paymentStatusOf,
  settleGate,
  type ArPaymentStatus,
} from '../lib/arPayments'

export type ArPaymentFilter = 'all' | 'active' | 'sold' | 'settled' | 'cancelled'

export const AR_PAYMENT_OPTIONS: { value: ArPaymentFilter; label: string }[] = [
  { value: 'all', label: 'All sales' },
  { value: 'active', label: 'Active' },
  { value: 'sold', label: 'Sold' },
  { value: 'settled', label: 'Settled' },
  { value: 'cancelled', label: 'Cancelled' },
]

export interface SalesArRow {
  dealId: string
  stockNumber: string
  vin: string
  vehicle: string
  customer: string
  saleDate: string | null
  /** Gross, GST-inclusive, paid to the seller. */
  purchase: number
  /** Gross, GST-inclusive, charged to the buyer. */
  sale: number
  gstOnSale: number
  gstCredit: number
  /** What the business actually remits on this sale: GST on sale less the credit. */
  netGst: number
  marginIncGst: number
  marginExGst: number
  /** True where the credit was capped by the GST on the sale - i.e. a loss-making sale. */
  capped: boolean  /**
   * realised once a sales invoice exists for the deal; projected while the sale price is
   * recorded but uninvoiced. The GST Register releases a Division 66 credit only on a
   * realised sale, and this screen must draw the same line or the two disagree.
   */
  basis: 'realised' | 'projected'
  treatment: GstTreatment
  treatmentLabel: string
  invoiceNumber: string | null
  invoiceId: string | null
  /**
   * The sale behind this receivable was amended after the documents were issued — the buyer
   * changed, or the price did. Winnie, Checks_20 Sep §8: "the finance and VA chase teams need
   * to know the buyer identity or amount they're about to chase against just changed, so they
   * don't act on stale information."
   */
  flaggedAmended: boolean
  flaggedAmendedReason: string | null
  /** What the buyer has actually paid against this sale. */
  received: number | null
  /** Sale less what has been received. Winnie's "Outstanding (Due)". */
  outstanding: number | null
  /**
   * Where this sale sits. Settled is the only one the platform sets itself, and
   * it is set when the money received reaches the sale price - see recordPayment.
   */
  status: 'active' | 'settled' | 'cancelled'
  settled: boolean
  /**
   * Unpaid / PartiallyPaid / FullyPaid, per the Vehicle Status Registry spec,
   * section 4. Distinct from `status` above, which is about the deal: a car can
   * be an active sale and part paid at the same time, and before this existed
   * there was no way for the report to say so.
   */
  paymentStatus: ArPaymentStatus
  /** True while the money is not all in, whatever the deal status says. */
  onChase: boolean
  /** When money was last received against this sale. */
  lastPaymentDate: string | null
}

export interface SalesArTotals {
  count: number
  purchase: number
  sale: number
  gstOnSale: number
  gstCredit: number
  netGst: number  /**
   * Split because only the realised half may reach a BAS. The Margin Scheme (GST
   * Register) reports exactly netGstRealised for the same period; the projected half is
   * a forecast off a recorded sale price with no invoice behind it yet.
   */
  netGstRealised: number
  netGstProjected: number
  marginIncGst: number
  marginExGst: number
  received: number
  outstanding: number
  invoiced: number
}

export interface SalesArReport {
  rows: SalesArRow[]
  periodLabel: string
  totals: SalesArTotals
  /** Sold deals carrying no sale price, so no money can be stated for them. */
  missingPrice: number
}

const r2 = (n: number) => Math.round(n * 100) / 100
const day = (v: unknown): string | null => (v ? String(v).slice(0, 10) : null)
const text = (v: unknown): string => (v == null ? '' : String(v).trim())

/**
 * Which statuses put a car on the receivables list.
 *
 * This used to be a hand-written ['sold', 'closed', 'cancelled'], and it predated the
 * 10 September change that made every sale land on sold_in_transit or sold_delivered.
 * Neither of those was in the list, so a car sold through the Sale Details Workspace
 * never appeared on Sales (AR) at all - a real receivable that the chase list did not
 * know existed. No row in staging exercised it yet only because the sales made through
 * that flow so far were test deals, since archived.
 *
 * It now reads the canonical list in lib/dealLifecycle, which is where "which statuses
 * mean sold" lives for the whole platform. Adding a sale stage there reaches this
 * screen on the same deploy.
 *
 * `cancelled` is not a sold status and is not in that list. It stays here because this
 * screen offers a Cancelled filter - a cancelled sale is something Winnie wants to be
 * able to look at - and it is carried as its own thing rather than smuggled in as a
 * kind of sale.
 */
const AR_STATUSES: readonly string[] = [...SOLD_STATUSES, 'cancelled']

export async function loadSalesAr(
  range: DateRange = ALL_TIME,
  payment: ArPaymentFilter = 'all',
  query = '',
): Promise<SalesArReport> {
  const { data, error } = await supabase
    .from('deals')
    .select(
      'id, deal_status, stock_number, sale_price, sale_date, sold_at, settled_at, valuation_amount, ' +
        'gst_treatment, seller_type, acquired_date, paid_at, created_at, ' +
        'buyer_name, buyer_company, ' +
        'vehicle_year, vehicle_make, vehicle_model, vehicle_vin, ' +
        'autograb_year, autograb_make, autograb_model, autograb_vin',
    )
    .in('deal_status', AR_STATUSES)
    .is('deleted_at', null)

  if (error) throw error

  const deals = (data || []) as unknown as Record<string, unknown>[]

  /**
   * Every invoice, rather than a filtered .in() on several hundred deal ids - that many
   * uuids in a query string is how a report starts failing on URL length once the ledger
   * grows. The invoice table is the sales ledger; it is the right size to read whole.
   */
  const invoiceByDeal = new Map<string, Record<string, unknown>>()
  const { data: invData, error: invError } = await supabase
    .from('invoices')
    .select(
      'id, deal_id, invoice_number, total_amount, payment_amount_received, payment_received_at, ' +
        'flagged_amended, flagged_amended_reason, cancelled_at',
    )

  if (invError) throw invError

  for (const inv of (invData || []) as unknown as Record<string, unknown>[]) {
    if (inv.deal_id) invoiceByDeal.set(String(inv.deal_id), inv)
  }

  const priced = deals.filter((d) => Number(d.sale_price ?? 0) > 0)
  const missingPrice = deals.length - priced.length

  const needle = query.trim().toLowerCase()

  const rows: SalesArRow[] = priced
    .map((d) => {
      const year = text(d.vehicle_year) || text(d.autograb_year)
      const make = text(d.vehicle_make) || text(d.autograb_make)
      const model = text(d.vehicle_model) || text(d.autograb_model)
      const vehicle = [year, make, model].filter(Boolean).join(' ') || 'Vehicle'

      const purchase = Number(d.valuation_amount ?? 0)
      const sale = Number(d.sale_price ?? 0)
      const saleDate = day(d.sale_date) || day(d.sold_at)

      const credit = assessCredit(
        d as unknown as Parameters<typeof assessCredit>[0],
        purchase,
        { salePrice: sale, saleDate },
      )

      const gstOnSale = gstComponent(sale)
      const gstCredit = credit.claimable
      const netGst = r2(gstOnSale - gstCredit)
      const marginIncGst = r2(sale - purchase)
      const marginExGst = r2(marginIncGst - netGst)

      const invoice = invoiceByDeal.get(String(d.id))
      const received = invoice ? r2(Number(invoice.payment_amount_received ?? 0)) : null
    /*
     * Unpaid / PartiallyPaid / FullyPaid against the SALE price, which is the
     * figure Outstanding (Due) is measured from and the one the settle gate in
     * dealStatusService uses. Measuring one thing two ways is how the AR list and
     * the reminders ladder came to disagree about whether a car was still owed.
     */
    const paymentStatus = paymentStatusOf(sale, received)
    const lastPaymentDate = invoice ? day(invoice.payment_received_at) : null

      /* Winnie's four filters, 10 Sep. Settled is set by recordPayment when the money
         received reaches the sale price. Active means the car is sold and the money is
         not in yet - her bracket said "sold-in transit/sold-delivered", but nothing in
         the database distinguishes in transit from delivered (there is no delivered_at
         and no transit flag), so Active is every live unsettled sale rather than a
         sub-split that would have to be invented. */
      const settled = Boolean(d.settled_at)
      const cancelled = String(d.deal_status || '').toLowerCase() === 'cancelled'
      const status: SalesArRow['status'] = cancelled
        ? 'cancelled'
        : settled
          ? 'settled'
          : 'active'

      return {
        dealId: String(d.id),
        stockNumber: text(d.stock_number),
        vin: text(d.vehicle_vin) || text(d.autograb_vin),
        vehicle,
        customer: text(d.buyer_company) || text(d.buyer_name),
        saleDate,
        purchase: r2(purchase),
        sale: r2(sale),
        gstOnSale: r2(gstOnSale),
        gstCredit: r2(gstCredit),
        netGst,
        marginIncGst,
        marginExGst,
        capped: credit.capped,
        basis: ((invoice && Number(invoice.total_amount ?? 0) > 0 ? 'realised' : 'projected') as SalesArRow['basis']),
        treatment: credit.treatment,
        treatmentLabel: GST_TREATMENT_LABEL[credit.treatment],
        invoiceNumber: invoice ? text(invoice.invoice_number) : null,
        invoiceId: invoice ? String(invoice.id) : null,
        flaggedAmended: Boolean(invoice && invoice.flagged_amended),
        flaggedAmendedReason: invoice ? (invoice.flagged_amended_reason as string) || null : null,
        received,
        /* Winnie, 10 Sep: "(Due) - this should show the Sale minus the paid amount."
           Deliberately the sale price, not the invoice total, so a car with no invoice
           raised yet still shows the whole sale as due rather than a blank. */
        outstanding: amountOutstanding(sale, received),
        status,
        settled,
        paymentStatus,
        onChase: onChaseList(paymentStatus),
        lastPaymentDate,
      }
    })
    .filter((r) => inRange(r.saleDate, range))
    .filter((r) => {
      /* 'sold' deliberately spans settled and unsettled - it is every car that has
         been sold. 'active' is the narrower one: sold and the money not in yet. If the
         two returned the same rows there would be no reason to have both. */
      if (payment === 'sold') return r.status !== 'cancelled'
      if (payment === 'active') return r.status === 'active'
      if (payment === 'settled') return r.status === 'settled'
      if (payment === 'cancelled') return r.status === 'cancelled'
      return true
    })
    .filter((r) => {
      if (!needle) return true
      return (
        r.stockNumber.toLowerCase().includes(needle) ||
        r.vin.toLowerCase().includes(needle) ||
        r.vehicle.toLowerCase().includes(needle) ||
        r.customer.toLowerCase().includes(needle) ||
        (r.invoiceNumber || '').toLowerCase().includes(needle)
      )
    })
    .sort((a, b) => (b.saleDate || '').localeCompare(a.saleDate || ''))

  const totals = rows.reduce<SalesArTotals>(
    (acc, r) => ({
      count: acc.count + 1,
      purchase: r2(acc.purchase + r.purchase),
      sale: r2(acc.sale + r.sale),
      gstOnSale: r2(acc.gstOnSale + r.gstOnSale),
      gstCredit: r2(acc.gstCredit + r.gstCredit),
      netGst: r2(acc.netGst + r.netGst),
      netGstRealised: r2(acc.netGstRealised + (r.basis === 'realised' ? r.netGst : 0)),
      netGstProjected: r2(acc.netGstProjected + (r.basis === 'projected' ? r.netGst : 0)),
      marginIncGst: r2(acc.marginIncGst + r.marginIncGst),
      marginExGst: r2(acc.marginExGst + r.marginExGst),
      received: r2(acc.received + (r.received ?? 0)),
      outstanding: r2(acc.outstanding + (r.outstanding ?? 0)),
      invoiced: acc.invoiced + (r.invoiceNumber ? 1 : 0),
    }),
    {
      count: 0,
      purchase: 0,
      sale: 0,
      gstOnSale: 0,
      gstCredit: 0,
      netGst: 0,
      netGstRealised: 0,
      netGstProjected: 0,
      marginIncGst: 0,
      marginExGst: 0,
      received: 0,
      outstanding: 0,
      invoiced: 0,
    },
  )

  return { rows, periodLabel: rangeLabel(range), totals, missingPrice }
}

/**
 * Record what the buyer has actually paid against a sale.
 *
 * Winnie, 10 Sep: "If total amount received matches the Sale column amount" - mark the
 * invoice with a green tick, change the status to Settled, and drop it off the AR
 * reminders list. All three of those are the same fact, so they are one write: settled_at
 * on the deal. The tick and the reminders list both read it rather than keeping their own
 * copy, because two copies of one fact is how they end up disagreeing.
 *
 * Settling does not touch commission. That was struck at Sold and is immutable - the
 * database trigger would refuse the write anyway. See build spec v2 section 4.
 */
export async function recordPayment(
  dealId: string,
  invoiceId: string | null,
  amountReceived: number,
  salePrice: number,
): Promise<{ ok: boolean; settled: boolean; message: string }> {
  const received = r2(Number(amountReceived))
  if (!Number.isFinite(received) || received < 0) {
    return { ok: false, settled: false, message: 'Enter the amount received as a number.' }
  }

  /*
   * With no invoice there is nowhere for the figure to live. The amount received is held on
   * the invoice row, and AR chases invoices rather than sales, so a payment recorded against
   * an uninvoiced car would be dropped on the floor - and if it happened to match the sale
   * price it would mark the deal Settled with nothing behind it. Refusing and naming the
   * missing step is the only honest answer.
   */
  if (!invoiceId) {
    return {
      ok: false,
      settled: false,
      message: 'No tax invoice has been raised for this sale yet, so there is nowhere to record a payment against. Raise the invoice first.',
    }
  }
  const sale = r2(Number(salePrice))
  if (received > sale) {
    return {
      ok: false,
      settled: false,
      message: 'That is more than the sale price. Check the figure before recording it.',
    }
  }

  /*
   * The AR Reminders ledger decides whether an invoice is still being chased from its
   * status, not from the amount - so writing the amount alone left the two screens
   * disagreeing: paid in full on this report, still Open and still showing a "Record
   * payment" button over there. The status moves with the money here for that reason.
   *
   * It is measured against the invoice total, which is what invoiceLedgerService does,
   * rather than against the sale price. The two are the same figure on a normal wholesale
   * deal; where they are not, the invoice is the document being chased and the sale price
   * is the commercial fact, so each is measured against its own.
   *
   * Correcting the figure back to nothing steps the status back to Issued, but only from a
   * payment state - a draft that was never issued must not be promoted to Issued by someone
   * clearing a typo.
   */
  const { data: invoice, error: readErr } = await supabase
    .from('invoices')
    .select('total_amount, status')
    .eq('id', invoiceId)
    .single()
  if (readErr) throw readErr

  const invoiceTotal = r2(Number((invoice as { total_amount?: number } | null)?.total_amount) || 0)
  const current = String((invoice as { status?: string } | null)?.status || '')
  const paidInFull = invoiceTotal > 0 && received + 0.005 >= invoiceTotal
  const nextStatus =
    received > 0
      ? paidInFull
        ? 'paid'
        : 'partial'
      : current === 'paid' || current === 'partial'
        ? 'sent'
        : current

  const { error: invErr } = await supabase
    .from('invoices')
    .update({
      payment_amount_received: received,
      payment_received_at: received > 0 ? new Date().toISOString() : null,
      status: nextStatus,
      updated_at: new Date().toISOString(),
    })
    .eq('id', invoiceId)
  if (invErr) throw invErr

  /* Settled the moment the money is all in, and un-settled if the figure is corrected
     downwards - a typo should not leave a car permanently marked paid.

     The test is the shared one. This used to re-implement it inline as
     `sale > 0 && received >= sale`, which is the same arithmetic today and is exactly the
     duplication arPayments.ts says it exists to prevent: two copies of the settlement rule
     that agree until one is changed. Found by the 14 September spec audit. */
  const fullyPaid = settleGate(sale, received).ok

  /*
   * Winnie, 18 September: the discharge is "triggered the moment payment_status reaches Fully
   * Paid". This is that moment - the one place on the platform where a receivable becomes
   * fully paid - so the discharge fires from here rather than from a screen somebody has to
   * open.
   *
   * Awaited, not fired and forgotten. The answer decides whether the vehicle may close out,
   * and a discharge whose result arrives after the settlement write would settle the car on
   * an outcome nobody had yet.
   */
  let dischargeNote = ''
  let ppsrDischarged = false
  if (fullyPaid) {
    const outcome = await dischargeOnFullyPaid(dealId)
    ppsrDischarged = outcome.discharged
    if (outcome.status === 'Failed') {
      dischargeNote =
        ' The PPSR discharge failed and needs a retry from Reports \u2192 PPSR Register: ' + outcome.message
    } else if (outcome.attempted && outcome.discharged) {
      dischargeNote = ' The PPSR registration was discharged.'
    }
  }

  /*
   * Whether being paid in full is enough to close the vehicle out depends on when it reached
   * sold-delivered, per the grandfather rule in lib/closeOut. A car that was already finished
   * before the gate went live settles on payment alone; one that reaches sold-delivered after
   * it also needs the discharge.
   */
  const [dates, dealRow] = await Promise.all([
    loadPlatformDates(),
    supabase.from('deals').select('reached_sold_delivered_at').eq('id', dealId).maybeSingle(),
  ])
  const verdict = closeOutVerdict({
    fullyPaid,
    ppsrDischarged,
    reachedSoldDeliveredAt:
      (dealRow.data as { reached_sold_delivered_at?: string | null } | null)?.reached_sold_delivered_at || null,
    gateGoLiveDate: dates.ppsrDischargeGateGoLive,
  })

  const settled = verdict.closedOut
  const { error: dErr } = await supabase
    .from('deals')
    .update({ settled_at: settled ? new Date().toISOString() : null })
    .eq('id', dealId)
  if (dErr) throw dErr

  return {
    ok: true,
    settled,
    message:
      (settled
        ? 'Paid in full - marked Settled and taken off AR reminders.'
        : fullyPaid
          ? 'Paid in full, but not settled. ' + verdict.reason
          : 'Payment recorded.') + dischargeNote,
  }
}

const cell = (v: unknown): string => {
  const s = v == null ? '' : String(v)
  return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s
}

export function salesArCsv(rows: SalesArRow[]): string {
  const head = [
    'Sale date',
    'Stock #',
    'VIN',
    'Vehicle',
    'Customer',
    'Purchase (inc GST)',
    'Sale (inc GST)',
    'GST on sale',
    'GST credit',
    'Net GST (margin)',
    'Margin (inc GST)',
    'Margin (ex GST)',
    'GST treatment',
    'Basis',
    'Credit capped',
    'Invoice #',
    'Received',
    'Outstanding',
  ]
  const body = rows.map((r) =>
    [
      r.saleDate || '',
      r.stockNumber,
      r.vin,
      r.vehicle,
      r.customer,
      r.purchase.toFixed(2),
      r.sale.toFixed(2),
      r.gstOnSale.toFixed(2),
      r.gstCredit.toFixed(2),
      r.netGst.toFixed(2),
      r.marginIncGst.toFixed(2),
      r.marginExGst.toFixed(2),
      r.treatmentLabel,
      r.basis === 'realised' ? 'Realised' : 'Projected',
      r.capped ? 'Yes' : 'No',
      r.invoiceNumber || '',
      r.received === null ? '' : r.received.toFixed(2),
      r.outstanding === null ? '' : r.outstanding.toFixed(2),
    ]
      .map(cell)
      .join(','),
  )
  return [head.map(cell).join(','), ...body].join('\n')
}
